// EMCR "Out-of-Jurisdiction Rescue" form pack (BC Emergency Management and Climate
// Readiness). Used by BC rescue societies reimbursed per task — first tenant: Eagle
// Valley Rescue (#1406). The manifest is consumed by both the SPA (renders the
// incident Forms tab + settings editor from these schemas) and the api (validates,
// prefills and builds the PDFs) — keep it free of runtime dependencies.

const RESPONSE_TYPES = [
    'Stood down enroute or on scene',
    'Extrication',
    'Embankment Rescue',
    'Water Rescue',
    'Fire Suppression'
];

const ROAD_CONDITIONS = ['Ice', 'Mud', 'Snow', 'Loose Gravel', 'Heavy Traffic'];

const WEATHER_CONDITIONS = ['Cloud', 'Fog', 'Rain', 'Smoke / Dust', 'Snow'];

const REQUESTING_AGENCIES = ['Police/Coroner', 'BCEHS', 'JRCC'];

const roundMoney = value => Math.round((Number(value) || 0) * 100) / 100;

// ERR reimbursement math (EMCR schedule 5.17). All Found Rate: "min. 1 hour then
// round to the NEAREST ¼ hour up or down" — deliberately different from incident
// salary hours, which round UP. Clean up Rate is defined by the form as an
// additional ¼ hour of the All Found Rate, so it is derived, not configured.
const computeErrClaim = ({hours, rescueServicesRendered, kms, rates = {}, additionalItems = []}) => {
    const allFoundRate = Number(rates.allFound) || 0;
    const roundedHours = Math.max(1, Math.round((Number(hours) || 0) * 4) / 4);
    const allFoundClaim = roundMoney(roundedHours * allFoundRate);
    const cleanUpClaim = rescueServicesRendered ? roundMoney(0.25 * allFoundRate) : 0;
    const consumablesClaim = rescueServicesRendered ? roundMoney(rates.consumables) : 0;
    const mileageClaim = roundMoney(Math.max(0, Number(kms) || 0) * (Number(rates.mileage) || 0));
    const additionalClaims = additionalItems.map(item => roundMoney(item && item.amount));
    const totalClaim = roundMoney(
        allFoundClaim + cleanUpClaim + consumablesClaim + mileageClaim
        + additionalClaims.reduce((sum, amount) => sum + amount, 0)
    );

    return {
        roundedHours,
        hrs: Math.floor(roundedHours),
        min: Math.round((roundedHours % 1) * 60),
        allFoundClaim,
        cleanUpClaim,
        consumablesClaim,
        mileageClaim,
        additionalClaims,
        totalClaim
    };
};

module.exports = {
    id: 'emcr',
    title: 'EMCR: Out-of-Jurisdiction Rescue',
    feature: 'emcrForms',
    incidentTypes: null,
    roster: {
        timeInOut: true,
        taskLeader: true,
        pii: ['location', 'emergencyContact']
    },
    forms: [
        {id: 'taskRegistration', title: 'Task Registration Form', usesRosterPii: true},
        {id: 'expenseReimbursement', title: 'Expense Reimbursement Request', usesRosterPii: false},
        {id: 'taskReport', title: 'Task Report Form', usesRosterPii: false}
    ],
    fields: [
        {key: 'taskNumber', label: 'Task #', type: 'text', section: 'Task', defaultFrom: 'incidentNumber'},
        {key: 'fileNumber', label: 'RCC/RCMP/BCAS File #', type: 'text', section: 'Task'},
        {key: 'errNumber', label: 'ERR #', type: 'text', section: 'Task'},
        {key: 'requestingAgency', label: 'Requesting Agency', type: 'select', section: 'Task', options: REQUESTING_AGENCIES},
        {key: 'taskSummary', label: 'Task Description', type: 'text', section: 'Task', helper: 'Short description for the Task Registration form, e.g. MVI'},
        {key: 'responseTypes', label: 'Type of Response', type: 'checkbox', multiple: true, section: 'Response', options: RESPONSE_TYPES},
        {key: 'responseTypeOther', label: 'Type of Response — Other', type: 'text', section: 'Response'},
        {key: 'roadConditions', label: 'Aggravating Road Conditions', type: 'checkbox', multiple: true, section: 'Conditions', options: ROAD_CONDITIONS},
        {key: 'roadConditionsOther', label: 'Road Conditions — Other', type: 'text', section: 'Conditions'},
        {key: 'weatherConditions', label: 'Aggravating Weather Conditions', type: 'checkbox', multiple: true, section: 'Conditions', options: WEATHER_CONDITIONS},
        {key: 'weatherConditionsOther', label: 'Weather Conditions — Other', type: 'text', section: 'Conditions'},
        {key: 'details', label: 'Details of the Task', type: 'text', multiline: true, section: 'Narrative', defaultFrom: 'notes', helper: 'Include all activities performed at scene'},
        {key: 'equipmentUsed', label: 'Equipment Used', type: 'text', multiline: true, section: 'Narrative'},
        {key: 'equipmentRepairRequest', label: 'Equipment Repair/Replacement Request (ER/RR)', type: 'checkbox', section: 'Narrative'},
        {key: 'rescueServicesRendered', label: 'Rescue services rendered', type: 'checkbox', section: 'Reimbursement', helper: 'Adds the Clean up Rate (¼ hour of the All Found Rate) and the Consumable Supplies Rate to the claim'},
        {key: 'kms', label: 'Distance travelled beyond 150 km', type: 'number', section: 'Reimbursement'},
        {key: 'additionalItem1Label', label: 'Additional item 1', type: 'text', section: 'Reimbursement'},
        {key: 'additionalItem1Amount', label: 'Additional item 1 claim ($)', type: 'number', section: 'Reimbursement'},
        {key: 'additionalItem2Label', label: 'Additional item 2', type: 'text', section: 'Reimbursement'},
        {key: 'additionalItem2Amount', label: 'Additional item 2 claim ($)', type: 'number', section: 'Reimbursement'}
    ],
    // Each tenant-config default declares which forms it feeds (`forms`) and its
    // grid width in 12ths (`width`) so the settings UI can group defaults under
    // each form and lay them out cleanly (e.g. city/province/postal on one row).
    tenantConfig: [
        {key: 'organizationName', label: 'Organization / Claimant Name', type: 'text', forms: ['expenseReimbursement', 'taskReport'], width: 12},
        {key: 'mailingAddress', label: 'Mailing Address', type: 'text', forms: ['expenseReimbursement'], width: 12},
        {key: 'city', label: 'City', type: 'text', forms: ['expenseReimbursement'], width: 5},
        {key: 'province', label: 'Province', type: 'text', forms: ['expenseReimbursement'], width: 3},
        {key: 'postalCode', label: 'Postal Code', type: 'text', forms: ['expenseReimbursement'], width: 4},
        {key: 'telephone', label: 'Telephone', type: 'text', forms: ['expenseReimbursement'], width: 6},
        {key: 'email', label: 'Email', type: 'text', forms: ['expenseReimbursement'], width: 6},
        {key: 'region', label: 'EMCR Region', type: 'text', forms: ['taskReport', 'taskRegistration'], width: 6},
        {key: 'signatoryTitle', label: 'Signatory Title', type: 'text', forms: ['expenseReimbursement', 'taskReport'], width: 6, helper: 'Printed as the Title on signed forms, e.g. Chief'},
        {key: 'rates.allFound', label: 'All Found Rate ($/hr)', type: 'number', forms: ['expenseReimbursement'], width: 4},
        {key: 'rates.consumables', label: 'Consumable Supplies Rate ($)', type: 'number', forms: ['expenseReimbursement'], width: 4},
        {key: 'rates.mileage', label: 'Mileage Rate ($/km beyond 150)', type: 'number', forms: ['expenseReimbursement'], width: 4}
    ],
    computeErrClaim,
    RESPONSE_TYPES,
    ROAD_CONDITIONS,
    WEATHER_CONDITIONS,
    REQUESTING_AGENCIES
};
